import { displayText, buildEmptyParty, hydrateTargetMarketCodes } from "./gpsrFormUtils";
import { createFibreId } from "./fibreCompositionData";
import { GPSR_PRODUCT_TYPE_CLOTHING_TEXTILE } from "./gpsrLabelPayload";
import { normalizeGpsrRepresentativeMode } from "./gpsrSelectOptions";
import { toHydratedWeightInput } from "../CreatePpwrLabelPage/packagingCompositionData";

export { GPSR_PRODUCT_TYPE_CLOTHING_TEXTILE };

export const EMPTY_GPSR_PRODUCT_SELECTS = {
  weightUnits: [],
  labelStatuses: [],
  representativeTypes: [],
  productCategories: [],
  fibreComponentTypes: [],
  fibreTypes: [],
  useRestrictions: [],
  minimumUserAges: [],
  careSymbols: [],
};

export function normalizeProductLabelList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

export function resolveProductLabelCountrySpecificsId(label) {
  if (label == null || typeof label !== "object") return null;
  if (label.country_specifics_id != null && label.country_specifics_id !== "") {
    return label.country_specifics_id;
  }
  const relation = label.country_specifics;
  if (relation == null || relation === "") return null;
  if (typeof relation !== "object") {
    return relation;
  }
  if (relation.id != null && relation.id !== "") {
    return relation.id;
  }
  return null;
}

export function findProductLabelForProduct(labels, productId) {
  if (productId == null || productId === "") return null;
  const list = Array.isArray(labels)
    ? labels
    : normalizeProductLabelList(labels);
  const productKey = String(productId);
  return (
    list.find((label) => {
      const relationId = resolveProductLabelCountrySpecificsId(label);
      if (relationId == null || relationId === "") return false;
      return String(relationId) === productKey;
    }) || null
  );
}

export function normalizeGpsrProductLabelStatus(status) {
  return String(status ?? "")
    .trim()
    .toLowerCase();
}

export function formatGpsrProductLabelStatusLabel(status) {
  const normalized = normalizeGpsrProductLabelStatus(status);
  if (!normalized) return "";
  if (normalized === "draft") return "Draft";
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function getProductDigitalLabelActionLabel(labelStatus) {
  const normalized = normalizeGpsrProductLabelStatus(labelStatus);
  if (!normalized) return "Create digital label";
  if (normalized === "draft") return "View draft";
  return "View digital label";
}

export function resolveGpsrProductLabelFilePaths(label) {
  if (label == null || typeof label !== "object") {
    return {
      brandLogo: null,
      productPhoto: null,
      declarationOfConformity: null,
    };
  }
  const normalize = (value) => {
    if (value == null) return null;
    const text = String(value).trim();
    return text ? text : null;
  };
  return {
    brandLogo: normalize(label.label_file),
    productPhoto: normalize(label.photo_product),
    declarationOfConformity: normalize(label.declaration_of_conformity),
  };
}

export function resolveGpsrProductLabelDigitalIdentity(label) {
  if (label == null || typeof label !== "object") {
    return { uuid: null, qrCode: null, qrCodeImage: null };
  }
  const normalize = (value) => {
    if (value == null) return null;
    const text = String(value).trim();
    return text ? text : null;
  };
  return {
    uuid: normalize(label.uuid),
    qrCode: normalize(label.qr_code),
    qrCodeImage: normalize(label.qr_code_image),
  };
}

const toMappedString = (value) => {
  if (value == null || value === "") return "";
  return String(value);
};

const pickMapped = (apiValue, defaultValue) => {
  const mapped = toMappedString(apiValue);
  if (mapped) return mapped;
  return toMappedString(defaultValue);
};

const mapPartyFromApi = (
  {
    companyName,
    streetAddress,
    postalCode,
    city,
    country,
    email,
  },
  defaults = {}
) => ({
  companyName: pickMapped(companyName, defaults.companyName),
  streetAddress: pickMapped(streetAddress, defaults.streetAddress),
  postalCode: pickMapped(postalCode, defaults.postalCode),
  city: pickMapped(city, defaults.city),
  country: pickMapped(country, defaults.country),
  email: pickMapped(email, defaults.email),
});

const partyHasAnyValue = (party = {}) =>
  Object.values(party).some((value) => displayText(value) !== "");

export function mapProductLabelDetailToGpsrForm(label, defaults = {}) {
  const traceabilityDefaults =
    defaults.traceability && typeof defaults.traceability === "object"
      ? defaults.traceability
      : {};
  const manufacturerDefaults =
    defaults.manufacturer && typeof defaults.manufacturer === "object"
      ? defaults.manufacturer
      : {};
  const representativeDefaults =
    defaults.representative && typeof defaults.representative === "object"
      ? defaults.representative
      : {};
  const importerDefaults =
    defaults.importer && typeof defaults.importer === "object"
      ? defaults.importer
      : {};
  const safetyDefaults =
    defaults.safety && typeof defaults.safety === "object"
      ? defaults.safety
      : {};

  const manufacturer = {
    businessName: pickMapped(
      label?.business_name,
      manufacturerDefaults.businessName
    ),
    ...mapPartyFromApi(
      {
        companyName: label?.manufacturer_company_name,
        streetAddress: label?.manufacturer_address_line,
        postalCode: label?.manufacturer_postal_code,
        city: label?.manufacturer_city,
        country: label?.manufacturer_country ?? label?.manufacturer_country_id,
        email: label?.manufacturer_email,
      },
      manufacturerDefaults
    ),
  };

  const customRepresentative = mapPartyFromApi(
    {
      companyName: label?.representative_company_name,
      streetAddress: label?.representative_address_line,
      postalCode: label?.representative_postal_code,
      city: label?.representative_city,
      country:
        label?.representative_country ?? label?.representative_country_id,
      email: label?.representative_email,
    },
    representativeDefaults.custom &&
      typeof representativeDefaults.custom === "object"
      ? representativeDefaults.custom
      : buildEmptyParty()
  );

  const rawType = label?.representative_type;
  const normalizedMode = normalizeGpsrRepresentativeMode(rawType);
  let representative;
  if (rawType == null || String(rawType).trim() === "") {
    representative = {
      enabled: false,
      mode:
        normalizeGpsrRepresentativeMode(representativeDefaults.mode) ===
        "custom"
          ? "custom"
          : "lappa",
      custom: customRepresentative,
    };
  } else {
    representative = {
      enabled: true,
      mode: normalizedMode === "custom" ? "custom" : "lappa",
      custom: customRepresentative,
    };
  }

  const importerParty = mapPartyFromApi(
    {
      companyName: label?.importer_company_name,
      streetAddress: label?.importer_address_line,
      postalCode: label?.importer_postal_code,
      city: label?.importer_city,
      country: label?.importer_country ?? label?.importer_country_id,
      email: label?.importer_email,
    },
    importerDefaults
  );
  const importerEnabledExplicit =
    typeof importerDefaults.enabled === "boolean"
      ? importerDefaults.enabled
      : false;
  const importer = {
    enabled: partyHasAnyValue(importerParty) || importerEnabledExplicit,
    ...importerParty,
  };

  const approvedUseRestrictions = Array.isArray(
    label?.approved_use_restrictions
  )
    ? label.approved_use_restrictions
        .map((item) => toMappedString(item))
        .filter(Boolean)
    : Array.isArray(safetyDefaults.approvedUseRestrictions)
      ? safetyDefaults.approvedUseRestrictions
      : [];

  return {
    id: label?.id ?? null,
    countrySpecificsId: resolveProductLabelCountrySpecificsId(label),
    status: toMappedString(label?.status),
    traceability: {
      productName: pickMapped(
        label?.product_name,
        traceabilityDefaults.productName
      ),
      batchLotNumber: pickMapped(
        label?.batch,
        traceabilityDefaults.batchLotNumber
      ),
      size: pickMapped(label?.size, traceabilityDefaults.size),
      grossWeight: toHydratedWeightInput(
        pickMapped(label?.gross_weight, traceabilityDefaults.grossWeight)
      ),
      grossWeightUnit: pickMapped(
        label?.gross_weight_unit,
        traceabilityDefaults.grossWeightUnit
      ),
      productType: pickMapped(
        label?.product_type,
        traceabilityDefaults.productType
      ),
      expiryDate: pickMapped(
        label?.expiry_date,
        traceabilityDefaults.expiryDate
      ),
      countryOfOrigin: pickMapped(
        label?.country_of_origin ?? label?.country_of_origin_id,
        traceabilityDefaults.countryOfOrigin
      ),
      barcode: pickMapped(label?.barcode, traceabilityDefaults.barcode),
      colourVariant: pickMapped(
        label?.colour_variant,
        traceabilityDefaults.colourVariant
      ),
      model: pickMapped(label?.model, traceabilityDefaults.model),
      gpcCode: pickMapped(label?.gpc_code, traceabilityDefaults.gpcCode),
      sku: toMappedString(traceabilityDefaults.sku),
      targetMarket: hydrateTargetMarketCodes(
        label?.target_market ?? traceabilityDefaults.targetMarket
      ),
    },
    manufacturer,
    representative,
    importer,
    safety: {
      safetyInformation: pickMapped(
        label?.safety_information,
        safetyDefaults.safetyInformation
      ),
      approvedUseRestrictions,
      maximumLiquidTemperature: pickMapped(
        label?.maximum_liquid_temperature,
        safetyDefaults.maximumLiquidTemperature
      ),
      other: pickMapped(label?.other, safetyDefaults.other),
      minimumUserAge: pickMapped(
        label?.minimum_user_age,
        safetyDefaults.minimumUserAge
      ),
      careSymbols: Array.isArray(label?.care_symbols)
        ? label.care_symbols.map((item) => toMappedString(item)).filter(Boolean)
        : Array.isArray(safetyDefaults.careSymbols)
          ? safetyDefaults.careSymbols
          : [],
      additionalCareInstructions: pickMapped(
        label?.additional_care_instructions,
        safetyDefaults.additionalCareInstructions
      ),
    },
  };
}

export function mapGpsrFibrePercentageFromApi(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

export function isGpsrFibreComponentMutationResponse(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return false;
  }
  return payload.id != null && String(payload.id).trim() !== "";
}

export function mapGpsrFibreComponentFromApi(apiComponent) {
  if (apiComponent == null || typeof apiComponent !== "object") {
    return null;
  }

  const fibresRaw = Array.isArray(apiComponent.fibres)
    ? apiComponent.fibres
    : [];
  const fibres =
    fibresRaw.length > 0
      ? fibresRaw.map((fibre) => ({
          id: createFibreId(),
          fibreType: toMappedString(fibre?.fibre_type),
          percentage: mapGpsrFibrePercentageFromApi(fibre?.percentage),
        }))
      : [
          {
            id: createFibreId(),
            fibreType: "",
            percentage: "",
          },
        ];

  const componentId =
    apiComponent.id != null && String(apiComponent.id).trim() !== ""
      ? String(apiComponent.id)
      : createFibreId("gpsr-textile-component");

  return {
    id: componentId,
    componentType: toMappedString(apiComponent.component_type),
    fibres,
  };
}

export function mapGpsrFibreComponentsFromApi(payload) {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
  return list
    .map((item) => mapGpsrFibreComponentFromApi(item))
    .filter(Boolean);
}

export function resolveProductSelectLabel(options, value) {
  const raw = displayText(value);
  if (!raw) return "";
  const match = (options || []).find(
    (option) => String(option?.value) === String(raw)
  );
  return match?.label != null && String(match.label).trim() !== ""
    ? String(match.label)
    : raw;
}

export function normalizeProductSelects(payload) {
  const source =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload
      : {};
  const next = { ...source };
  Object.keys(EMPTY_GPSR_PRODUCT_SELECTS).forEach((key) => {
    next[key] = Array.isArray(source[key]) ? source[key] : [];
  });
  return next;
}

export function isGpsrClothingTextileProductType(productType) {
  return (
    displayText(productType) === GPSR_PRODUCT_TYPE_CLOTHING_TEXTILE
  );
}
